import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import './AdminTermOutcomeFinalization.css';
import AcademicYearSelector from './AcademicYearSelector';

export default function AdminTermOutcomeFinalization() {
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [programId, setProgramId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  const loadPersistedProgress = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/course-outcome-finalizations/progress`,
      );
      const data = response.data || {};
      setBulkProgress((previous) => {
        if (previous && ['loading', 'running'].includes(previous.phase)) return previous;
        return {
          phase: 'idle',
          total: Number(data.total_courses || 0),
          processed: 0,
          finalized: Number(data.finalized_courses || 0),
          calculated: Number(data.calculated_courses || 0),
          noData: Number(data.no_data_courses || 0),
          alreadyClosed: Number(data.finalized_courses || 0),
          blocked: 0,
          currentCourse: '',
          programsLoaded: 0,
          programTotal: 0,
          remaining: Number(data.remaining_courses || 0),
        };
      });
    } catch {
      // İlerleme özeti yardımcı bilgidir; ana ekranı kullanılamaz yapma.
    }
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/programs`)
      .then((response) => setPrograms(response.data || []))
      .catch(() => setMessage('Programlar yüklenemedi. Sayfayı yenileyip tekrar deneyin.'));
  }, []);

  useEffect(() => { loadPersistedProgress(); }, [loadPersistedProgress]);

  useEffect(() => {
    if (!programId) {
      setCourses([]);
      return;
    }
    axios.get(`${API_BASE_URL}/api/courses?programId=${programId}`)
      .then((response) => {
        setCourses(response.data || []);
        if (!(response.data || []).length) {
          setMessage('Seçili akademik yılda bu programa ait ders bulunmuyor.');
        }
      })
      .catch((error) => setMessage(error.response?.data?.error || 'Dersler yüklenemedi.'));
    setCourseId('');
    setAnalysis(null);
  }, [programId]);

  const preview = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pre-midterm/term-final-class-breakdown?course_id=${courseId}`,
      );
      const data = response.data;
      setAnalysis(data);
      const calculatedResultCount = (data?.students || []).reduce(
        (total, student) => total
          + (student.learning_outcomes?.length || 0)
          + (student.program_outcomes?.length || 0),
        0,
      );
      if (data?.calculation_source === 'finalized_snapshot') {
        setMessage(data.closure_type === 'no_data'
          ? 'Bu ders daha önce verisiz olarak nihai kapatılmış.'
          : 'Bu dersin sonuçları daha önce kesinleştirilmiş ve değiştirilemez.');
      } else if (!data?.finalization_readiness?.ready) {
        setMessage(`${data.finalization_readiness.blocking_count} kritik kapanış kontrolü tamamlanmadı.`);
      } else if (!calculatedResultCount) {
        setMessage('Tamamlanmış ÖÇ/PÇ verisi yok; ders verisiz kapanış olarak kesinleştirilebilir.');
      } else {
        setMessage('Ders dönem sonu kesinleştirmesine hazır.');
      }
    } catch (error) {
      setAnalysis(null);
      setMessage(error.response?.data?.error || 'Dönem sonuçları hesaplanamadı.');
    }
  };

  const finalizeCourse = async (targetCourseId) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/course-outcome-finalizations`,
      { course_id: Number(targetCourseId) },
    );
    return response.data;
  };

  const finalize = async () => {
    if (!analysis) return;
    const calculatedResultCount = (analysis.students || []).reduce(
      (total, student) => total
        + (student.learning_outcomes?.length || 0)
        + (student.program_outcomes?.length || 0),
      0,
    );
    if (!analysis.finalization_readiness?.ready) return;
    const confirmText = calculatedResultCount
      ? 'Mevcut ve tamamlanmış sınav verileriyle dönem sonuçları kesinleştirilecek. Eksik sınav türleri hesaba katılmayacak. Devam?'
      : 'Bu ders tamamlanmış ÖÇ/PÇ verisi bulunmadan nihai olarak kapatılacak. Devam?';
    if (!window.confirm(confirmText)) return;
    setSaving(true);
    try {
      const result = await finalizeCourse(courseId);
      setMessage(result.closure_type === 'no_data'
        ? 'Ders, hesaplanabilir sınav verisi bulunmadığı kaydıyla nihai olarak kapatıldı.'
        : `${result.student_count} öğrencinin ${result.result_count} ÖÇ/PÇ sonucu tek işlemde kesinleştirildi.`);
      await loadPersistedProgress();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Kayıt başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const finalizeAll = async () => {
    if (!window.confirm('Seçili akademik yıldaki hazır derslerin dönem sonuçları kesinleştirilecek. Hazır olmayan dersler atlanacak. Devam?')) return;
    setSaving(true);
    setMessage('');
    setBulkProgress({
      phase: 'loading', total: 0, processed: 0, finalized: 0,
      calculated: 0, noData: 0, alreadyClosed: 0, blocked: 0,
      currentCourse: '', programsLoaded: 0, programTotal: programs.length,
    });
    try {
      let courseCount = 0;
      let total = 0;
      let noDataCount = 0;
      let alreadyClosedCount = 0;
      const blockedCourses = [];
      const allCoursesById = new Map();

      // Önce toplamı belirle. Böylece kullanıcı işlem başlar başlamaz gerçek
      // payda üzerinden hem tarama hem kesinleşme yüzdesini görebilir.
      for (let programIndex = 0; programIndex < programs.length; programIndex += 1) {
        const program = programs[programIndex];
        const courseResponse = await axios.get(`${API_BASE_URL}/api/courses?programId=${program.id}`);
        for (const course of (courseResponse.data || [])) {
          allCoursesById.set(String(course.id), course);
        }
        setBulkProgress((previous) => ({
          ...previous,
          programsLoaded: programIndex + 1,
        }));
      }

      const allCourses = Array.from(allCoursesById.values());
      const counters = {
        processed: 0, finalized: 0, calculated: 0,
        noData: 0, alreadyClosed: 0, blocked: 0,
      };
      setBulkProgress((previous) => ({
        ...previous,
        phase: 'running',
        total: allCourses.length,
        currentCourse: '',
      }));

      for (const course of allCourses) {
        const courseLabel = `${course.code || course.course_code || '-'} - ${course.name || course.course_name || '-'}`;
        setBulkProgress((previous) => ({
          ...previous,
          currentCourse: courseLabel,
        }));
        try {
          const analysisResponse = await axios.get(
            `${API_BASE_URL}/api/pre-midterm/term-final-class-breakdown?course_id=${course.id}`,
          );
          const courseAnalysis = analysisResponse.data;
          if (courseAnalysis.calculation_source === 'finalized_snapshot') {
            alreadyClosedCount += 1;
            counters.alreadyClosed += 1;
            counters.finalized += 1;
            continue;
          }
          if (!courseAnalysis.finalization_readiness?.ready) {
            blockedCourses.push(course.code || course.course_code || course.name || String(course.id));
            counters.blocked += 1;
            continue;
          }
          try {
            const result = await finalizeCourse(course.id);
            courseCount += 1;
            total += Number(result.result_count || 0);
            counters.finalized += 1;
            if (result.closure_type === 'no_data') {
              noDataCount += 1;
              counters.noData += 1;
            } else {
              counters.calculated += 1;
            }
          } catch (error) {
            if (error.response?.status === 409
                && String(error.response?.data?.error || '').includes('daha önce kesinleştirilmiş')) {
              alreadyClosedCount += 1;
              counters.alreadyClosed += 1;
              counters.finalized += 1;
              continue;
            }
            throw error;
          }
        } finally {
          counters.processed += 1;
          setBulkProgress((previous) => ({
            ...previous,
            ...counters,
          }));
        }
      }
      setBulkProgress((previous) => ({
        ...previous,
        ...counters,
        phase: 'completed',
        currentCourse: '',
      }));
      const completed = courseCount
        ? `${courseCount} ders için ${total} sonuç kesinleştirildi.`
        : 'Kesinleştirmeye hazır ders bulunmuyor.';
      const blocked = blockedCourses.length
        ? ` ${blockedCourses.length} ders kapanış eksikleri nedeniyle atlandı: ${blockedCourses.slice(0, 20).join(', ')}${blockedCourses.length > 20 ? '…' : ''}.`
        : '';
      const noData = noDataCount ? ` ${noDataCount} ders verisiz kapatıldı.` : '';
      const alreadyClosed = alreadyClosedCount ? ` ${alreadyClosedCount} ders önceden kapatılmıştı.` : '';
      setMessage(completed + noData + alreadyClosed + blocked);
    } catch (error) {
      setBulkProgress((previous) => previous ? ({
        ...previous,
        phase: 'error',
      }) : null);
      setMessage(error.response?.data?.error || error.message || 'Toplu işlem başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const readiness = analysis?.finalization_readiness;
  const calculatedResultCount = (analysis?.students || []).reduce(
    (total, student) => total
      + (student.learning_outcomes?.length || 0)
      + (student.program_outcomes?.length || 0),
    0,
  );
  const alreadyFinalized = analysis?.calculation_source === 'finalized_snapshot';
  const noDataClosure = Boolean(analysis) && !alreadyFinalized
    && calculatedResultCount === 0 && readiness?.ready;
  const canFinalize = Boolean(analysis) && !alreadyFinalized && readiness?.ready;
  const processedPercent = bulkProgress?.total
    ? Math.round((bulkProgress.processed / bulkProgress.total) * 100)
    : 0;
  const finalizedPercent = bulkProgress?.total
    ? Math.round((bulkProgress.finalized / bulkProgress.total) * 100)
    : 0;

  return (
    <div className="term-results">
      <div className="term-hero">
        <span>Yönetici İşlemi</span>
        <h2>Dönem Sonuçları</h2>
        <p>İşlem yapılacak akademik yılı seçin; yalnızca kapanış kontrollerini geçen dersleri kesinleştirin.</p>
        <div className="term-year"><AcademicYearSelector /></div>
        <button className="term-primary" onClick={finalizeAll} disabled={saving}>
          {saving ? 'İşleniyor…' : 'Bu yılın hazır sonuçlarını kesinleştir'}
        </button>
        {bulkProgress && (
          <div className={`term-bulk-progress is-${bulkProgress.phase}`} aria-live="polite">
            <div className="term-progress-heading">
              <strong>
                {bulkProgress.phase === 'loading'
                  ? 'Ders listesi hazırlanıyor'
                  : bulkProgress.phase === 'idle'
                    ? 'Mevcut kesinleşme durumu'
                  : bulkProgress.phase === 'completed'
                    ? 'Toplu kesinleştirme tamamlandı'
                    : bulkProgress.phase === 'error'
                      ? 'İşlem kesildi'
                      : 'Dönem sonuçları kesinleştiriliyor'}
              </strong>
              <b>%{finalizedPercent} kesinleşti</b>
            </div>
            {bulkProgress.phase === 'loading' ? (
              <p>
                Programlar okunuyor: {bulkProgress.programsLoaded}/{bulkProgress.programTotal}
              </p>
            ) : (
              <>
                <div
                  className="term-progress-track"
                  role="progressbar"
                  aria-label="Kesinleşen ders oranı"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={finalizedPercent}
                >
                  <div className="term-progress-fill" style={{ width: `${finalizedPercent}%` }} />
                </div>
                <div className="term-progress-counts">
                  <span><b>{bulkProgress.finalized}</b> / {bulkProgress.total} kesinleşti</span>
                  {bulkProgress.phase === 'idle'
                    ? <span><b>{Math.max(bulkProgress.total - bulkProgress.finalized, 0)}</b> ders kaldı</span>
                    : <span><b>{bulkProgress.processed}</b> / {bulkProgress.total} incelendi (%{processedPercent})</span>}
                  <span><b>{bulkProgress.calculated}</b> hesaplandı</span>
                  <span><b>{bulkProgress.noData}</b> verisiz kapandı</span>
                  {bulkProgress.phase !== 'idle' && <span><b>{bulkProgress.alreadyClosed}</b> önceden kapalı</span>}
                  {bulkProgress.phase !== 'idle' && <span><b>{bulkProgress.blocked}</b> atlandı</span>}
                </div>
                {bulkProgress.currentCourse && (
                  <p className="term-progress-current" title={bulkProgress.currentCourse}>
                    Şu an: {bulkProgress.currentCourse}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="term-detail">
        <h3>Ders kontrolü</h3>
        <div className="term-controls">
          <select value={programId} onChange={(event) => setProgramId(event.target.value)}>
            <option value="">Program seçin</option>
            {programs.map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}
          </select>
          <select value={courseId} onChange={(event) => setCourseId(event.target.value)} disabled={!programId}>
            <option value="">Ders seçin</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.code || course.course_code || '-'} - {course.name || course.course_name || '-'}
              </option>
            ))}
          </select>
          <button onClick={preview} disabled={!courseId}>Önizle</button>
        </div>

        {analysis && (
          <>
            <div className="term-summary">
              <b>{analysis.students?.length || 0}</b> öğrenci
              <span>Sınıf genel: %{analysis.class_averages?.overall_term_final_pct ?? '-'}</span>
              <button
                className="term-outline"
                onClick={finalize}
                disabled={saving || !canFinalize}
              >
                Bu dersi kesinleştir
              </button>
            </div>

            {noDataClosure && (
              <div className="term-readiness is-ready">
                <h4>Verisiz kapanış</h4>
                <div className="term-check-list">
                  <div className="term-check is-pass">
                    <span aria-hidden="true">✓</span>
                    <p>Tamamlanmış ve ÖÇ/PÇ üretilebilen sınav verisi yok. Ders gerekçesiyle birlikte kapatılabilir.</p>
                  </div>
                </div>
              </div>
            )}

            {readiness && !noDataClosure && (
              <div className={`term-readiness ${readiness.ready ? 'is-ready' : 'has-errors'}`}>
                <h4>{readiness.ready ? 'Kesinleştirmeye hazır' : 'Kesinleştirme öncesi eksikler'}</h4>
                <div className="term-check-list">
                  {(readiness.checks || []).map((check) => (
                    <div className={`term-check is-${check.status}`} key={check.id}>
                      <span aria-hidden="true">{check.status === 'pass' ? '✓' : '!'}</span>
                      <p>{check.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="term-student-list">
              <h4>Öğrenci sonuçları</h4>
              <div className="term-student-head">
                <span>Öğrenci no</span><span>Öğrenci</span><span>Başarı</span><span>Çıktılar</span>
              </div>
              {(analysis.students || []).map((student) => (
                <details className="term-student-row" key={student.student_no}>
                  <summary>
                    <span>{student.student_no || '-'}</span>
                    <span>{student.student_name || '-'}</span>
                    <strong>%{student.overall_term_final_pct ?? '-'}</strong>
                    <span className="term-detail-link">ÖÇ / PÖÇ gör</span>
                  </summary>
                  <div className="term-outcome-detail">
                    <div>
                      <b>Öğrenme çıktıları</b>
                      {student.learning_outcomes?.length
                        ? student.learning_outcomes.map((outcome) => <span key={outcome.id}>{outcome.code}: %{outcome.pct}</span>)
                        : <em>Veri yok</em>}
                    </div>
                    <div>
                      <b>Program çıktıları</b>
                      {student.program_outcomes?.length
                        ? student.program_outcomes.map((outcome) => <span key={outcome.id}>{outcome.code}: %{outcome.pct}</span>)
                        : <em>Veri yok</em>}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </>
        )}
      </div>
      {message && <div className="term-message">{message}</div>}
    </div>
  );
}
